import { memo, useCallback } from 'react'

import { Cancel } from '@mui/icons-material'
import { Box, TextField } from '@mui/material'
import { VideoObject, VideoObjectInsert, VideoStreamObjectInsert } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  useSubmissionProgressStore,
  selectSubmissionProgressSetters,
  SubmissionProgress,
  SubmissionButton,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useVideoObjectCreateMutation } from 'hooks/mutations/content/VideoObject'
import { videoObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects'
import { workspaceQueryKeys } from 'services/queryKeys'

type FormStructure = VideoObjectInsert | VideoStreamObjectInsert

type NewDialogProps = {
  onSuccess?: (data: VideoObject) => void
  initialValue?: VideoObject
}

const NewDialogRaw = ({ onSuccess, initialValue }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const handleClose = useCloseHandler()

  const { workspaceId } = videoObjectsOverviewRoute.useParams()

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const { data: workspace } = workspaceQuery

  const { mutate: createVideoObject, ...videoObjectCreateProps } = useVideoObjectCreateMutation({})

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      repositoryId: workspace?.contentRepositoryId,
      ...initialValue,
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.Video.creatingVideoObject') })

      createVideoObject(
        { videoObjectInsert: data as VideoObjectInsert },
        {
          onSuccess: (data) => {
            setTimeout(() => {
              handleClose()

              if (onSuccess) {
                setTimeout(() => {
                  onSuccess(data)
                }, 300)
              }
            }, 700)
          },
          onError: () => {
            setCurrentStep({
              value: 100,
              description: `${t('domain:Workspace.Video.somethingWentWrong')}`,
              icon: <Cancel fontSize="small" color="error" />,
            })
          },
        },
      )
    },
    [setCurrentStep, t, createVideoObject, handleClose, onSuccess],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.Video.addVideoObject')}
      description={t('domain:Workspace.Video.videoObjectCreateDescription')}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={videoObjectCreateProps.isSuccess} isError={videoObjectCreateProps.isError} />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!videoObjectCreateProps.isSuccess && !videoObjectCreateProps.isError}
          successCondition={videoObjectCreateProps.isSuccess}
          errorCondition={videoObjectCreateProps.isError}
          loading={videoObjectCreateProps.isPending}
          disabled={videoObjectCreateProps.isPending || videoObjectCreateProps.isSuccess}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, my: 2 }}>
        <Controller
          control={control}
          name="name"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('name')}
              required
              helperText={fieldState.error?.message}
              error={!!fieldState.error}
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
